<template>
    <header ref="headerRef" class="header-menu flex">
        <LogoWrapper v-if="isDisplayModeFunnel" />
        <div v-if="!isDisplayModeFunnel" class="header-menu_items flex">
            <div data-testid="navbar-toggle-state" class="header-menu__toggle">
                <HeaderMenuItem
                    :title="isDisplayModeFull ? t('navbar.header.menu.reduce') : t('navbar.header.menu.expand')"
                    icon="header/burger"
                    :size="26"
                    :show-title="false"
                    @click="handleChangeNavBarDisplayMode"
                />

                <a v-if="isDisplayMobile" :href="asideMenuStore.logoNavigationRoute">
                    <MaltImg
                        key="expanded"
                        class="header-menu__logo-mobile"
                        src="/navbar/logos/malt-logo-red.svg?vh=9690b2"
                        width="100"
                        height="28"
                        alt="Malt logo"
                    />
                </a>
            </div>

            <HeaderSearchWrapper
                v-if="!isDisplayMobile && (searchFreelancerDisplayHandler || (userStore.hasSelectedClientIdentity && searchDisplayMode !== 'NONE'))"
            />

            <HeaderAvailability v-if="userStore.hasSelectedFreelanceIdentity" />

            <HeaderPunchout v-if="isPunchoutBadgeShownForCompany && isPunchoutEnabled" />

            <div class="header-menu_actions" data-testid="header-menu-actions">
                <div class="header-menu_actions">
                    <HeaderSalesContactDetails v-if="salesManagerDetails" :sales-manager="salesManagerDetails" />
                    <HeaderMenuItem v-for="menu of headerNavigationTabs" :key="menu.id" :tab="menu" />
                </div>
                <LanguageIndicator class="header-menu_actions_language-indicator" />
            </div>
        </div>

        <HeaderSingleLogout v-if="showSingleLogout" />

        <HeaderSearchWrapper
            v-if="
                isDisplayMobile &&
                !isDisplayModeFunnel &&
                (searchFreelancerDisplayHandler || (userStore.hasSelectedClientIdentity && searchDisplayMode !== 'NONE'))
            "
            class="isMobile"
        />
    </header>
</template>

<script setup lang="ts">
    import {computed, onMounted, ref, watch} from 'vue';
    import {useSearchRouteDefinitions} from '@navbar/features/search';
    import {SearchDisplayMode} from '@navbar/features/search/search.types';
    import {FetchError, type SalesManagerContactResource} from '@navbar-api';
    import {LogoWrapper} from '@navbar-logged/common/ui';
    import {useAsideMenuStore, useDisplayModeStore} from '@navbar-logged/features/aside-menu';
    import {HeaderAvailability} from '@navbar-logged/features/availability';
    import {useHeaderMenuStore} from '@navbar-logged/features/header-menu';
    import {LanguageIndicator} from '@navbar-logged/features/language-selector';
    import HeaderPunchout from '@navbar-logged/features/punchout/HeaderPunchout.vue';
    import {HeaderSearchWrapper} from '@navbar-logged/features/search';
    import HeaderSingleLogout from '@navbar-logged/features/single-logout/HeaderSingleLogout.vue';
    import {useUserStore} from '@navbar-logged/features/user';
    import {ClientSalesManagerContact, FreelanceCompanyContext, useFeatureFlagsStoreLogged} from '@navbar-registry';
    import {storeToRefs} from 'pinia';
    import {MaltImg} from '#components';
    import {useLogger, useTranslation} from '#imports';
    import {useDetectOutsideClick, useDisplayMobile} from '@malt/utils';
    import HeaderMenuItem from './HeaderMenuItem.vue';
    import HeaderSalesContactDetails from './HeaderSalesContactDetails.vue';

    const featureFlagsStoreLogged = useFeatureFlagsStoreLogged();
    const {features} = storeToRefs(featureFlagsStoreLogged);

    const {t} = useTranslation();
    const headerRef = ref<HTMLElement>();

    const logger = useLogger();

    const userStore = useUserStore();
    const {isDisplayMobile} = useDisplayMobile();
    const asideMenuStore = useAsideMenuStore();

    const headerMenuStore = useHeaderMenuStore();
    const {searchDisplayMode, headerNavigationTabs, searchPreviewTarget} = storeToRefs(headerMenuStore);

    const displayModeStore = useDisplayModeStore();
    const {isDisplayModeFunnel, isDisplayModeFull} = storeToRefs(displayModeStore);

    const isPunchoutBadgeShownForCompany = computed<boolean>(() => userStore.selectedIdentity?.punchoutDetails?.showPunchoutBadge === true);
    const isPunchoutEnabled = computed<boolean>(() => userStore.selectedIdentity?.punchoutDetails?.punchoutEnabled === true);
    const showSingleLogout = ref(false);

    const salesManagerDetails = ref<SalesManagerContactResource>();

    onMounted(async () => {
        if (isDisplayModeFunnel.value) {
            try {
                const freelancerCompany = await FreelanceCompanyContext.getFreelancerCompany();
                showSingleLogout.value = freelancerCompany.showLogoutButton;
            } catch (e) {
                logger.warn(e, 'Cannot fetch Sales manager contact details');
            }
        }
    });

    onMounted(async () => {
        try {
            salesManagerDetails.value = userStore.hasSelectedClientIdentity ? await ClientSalesManagerContact.getSalesManagerContact() : undefined;
        } catch (e) {
            // Some client companies simply have no sales manager contact to display.
            if (e instanceof FetchError && e.statusCode === 404) {
                salesManagerDetails.value = undefined;
                return;
            }

            logger.warn(e, 'Cannot fetch Sales manager contact details');
        }
    });

    function handleChangeNavBarDisplayMode() {
        if (isDisplayMobile.value) {
            asideMenuStore.toggleMobileNavBarOpened();
        } else {
            displayModeStore.toggleNavbarDisplayMode();
        }
    }

    const {currentPage} = useSearchRouteDefinitions();
    const searchFreelancerDisplayHandler = computed(() => userStore.hasSelectedFreelanceIdentity && currentPage.value?.name === 'Search');

    const {startDetecting, stopDetecting} = useDetectOutsideClick(
        headerRef,
        () => {
            if (!isDisplayMobile.value) {
                searchPreviewTarget.value = 'NONE';
            }
        },
        {immediate: false},
    );

    function headerMenuOverDetect() {
        if (searchDisplayMode.value === SearchDisplayMode.FORM) {
            startDetecting();
        } else {
            stopDetecting();
        }
    }

    watch(searchDisplayMode, () => headerMenuOverDetect(), {immediate: true});
</script>

<style lang="scss" scoped>
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    .header-menu {
        flex-direction: column;
        justify-content: center;
        width: calc(100vw - var(--layout-padding-left));
        height: var(--header-menu-height);
        padding: 0 var(--joy-core-spacing-4);
        background-color: var(--joy-color-neutral-0);
        position: fixed;
        top: 0;
        left: var(--layout-padding-left);
        z-index: 24;
        transition-property: width, left;
        transition-duration: var(--joy-transition-duration-default);
        transition-timing-function: ease;
        box-shadow: 0px 0 5px rgba(0, 0, 0, 0.1);

        @include mq.screen_sm {
            --layout-padding-left: 0px;
            padding: 0 var(--joy-core-spacing-2);
        }

        &_items {
            justify-content: space-between;
            align-items: center;
            flex: 1;
            flex-wrap: nowrap;
            position: relative;
        }

        &_actions {
            display: grid;
            grid-auto-flow: column;
            align-items: center;

            &_language-indicator {
                display: none;
                @include mq.screen_sm(min) {
                    display: block;
                }
            }
        }

        &__toggle {
            display: flex;
            align-items: center;

            img {
                display: block;
            }
        }

        &__toggle + .punchout-header {
            margin-left: auto;
        }

        &__logo-mobile {
            padding-right: var(--joy-core-spacing-2);
        }
    }
</style>
